/*
 * Copyright (c) Nexign, JSC, 2021-2024.
 */
/**
 * InquiryInfo Page chat history component
 *
 * @author Julia Ignatieva
 * @author Ilya Babanov
 * @author Ilya Baranchikov
 * @author Viacheslav Smalenski
 * @version 1.28.0
 * @since 1.15.0
 */

import { useCallback, useState } from 'react';

import moment from 'moment';
import { useSelector } from 'react-redux';

import { icons } from '@assets/index';
import { LinkBlock } from '@components/common';
import { ImgStyled } from '@components/common/Block/Img';
import { getTimeZone } from '@components/Table/helpers/getTimeZone';
import type { IPageInquiryInfoTranslation } from '@modules/internationalization/types/pages/inquiryInfo';
import { getPrintableText } from '@utils/formatText';

import { EFormatData } from '../../PageDashboard/types/widgetTypes';
import {
    inquiryInfoApiChatSelector,
    inquiryInfoApiIsInquiryClosedSelector,
    inquiryInfoApiIsShowAnswerBlockSelector,
    inquiryInfoApiIsShowApproveBlockSelector,
    inquiryInfoApiNoteMaxLengthSelector,
    inquiryInfoApiReportNoteSelector,
} from '../selectors';
import { EActionType, EUserType, IInquiryChatAction } from '../types/inquiryInfoType';
import InquiryInfoPageChatForm from './InquiryInfoPageChatForm';
import {
    InquiryInfoPageChatHistoryFinalLineStyled,
    InquiryInfoPageChatHistoryFinalLineTextStyled,
    InquiryInfoPageChatHistoryMissingBlockStyled,
    InquiryInfoPageChatHistoryNoteInfoStyled,
    InquiryInfoPageChatHistoryNoteStyled,
    InquiryInfoPageChatHistoryUserInfoStyled,
    InquiryInfoPageDateInfoStyled,
    InquiryInfoPageMissingChatHistoryStyled,
    InquiryInfoPageMissingChatHistoryTextStyled,
    InquiryInfoPageStyled,
} from './styled/InquiryInfoPageStyled';

export interface IInquiryInfoChatHistory {
    inquiryId: string;
    setIsShowLoader: (isShowLoader: boolean) => void;
    translationInquiryInfo: IPageInquiryInfoTranslation;
    allowedFileExtensions?: string[];
    maxFileCount?: number;
}

const InquiryInfoPageChatHistory = (props: IInquiryInfoChatHistory) => {
    const { inquiryId, translationInquiryInfo } = props;

    const [isShowMissingActions, setShowMissingActions] = useState(false);

    const reportNote = useSelector(inquiryInfoApiReportNoteSelector(inquiryId));
    const chat = useSelector(inquiryInfoApiChatSelector(inquiryId));
    const isShowAnswerBlock = useSelector(inquiryInfoApiIsShowAnswerBlockSelector(inquiryId));
    const isShowApproveBlock = useSelector(inquiryInfoApiIsShowApproveBlockSelector(inquiryId));
    const isInquiryClosed = useSelector(inquiryInfoApiIsInquiryClosedSelector(inquiryId));
    const noteMaxLength = useSelector(inquiryInfoApiNoteMaxLengthSelector(inquiryId));

    const getUserTypeTranslation = useCallback(
        (type: EActionType) => {
            if (type === EActionType.QUESTION || type === EActionType.QUESTION_OPEN) {
                return translationInquiryInfo.userType[EUserType.OPERATOR];
            }
            if (type === EActionType.ANSWER) {
                return translationInquiryInfo.userType[EUserType.CLIENT];
            }
            if (type === EActionType.REPORT_NOTE) {
                return translationInquiryInfo.labelFinalNote;
            }
            return translationInquiryInfo.userType[EUserType.DEFAULT];
        },
        [translationInquiryInfo],
    );

    const getChatHistoryAction = useCallback(
        (item: IInquiryChatAction, index?: number) => {
            return (
                <div key={item.type + index}>
                    <InquiryInfoPageChatHistoryNoteInfoStyled>
                        <InquiryInfoPageChatHistoryUserInfoStyled>
                            {getUserTypeTranslation(item.type)}
                        </InquiryInfoPageChatHistoryUserInfoStyled>
                        {item.date && item.type !== EActionType.REPORT_NOTE && (
                            <InquiryInfoPageDateInfoStyled>
                                {moment(item.date).format(EFormatData.FullDateWithTime)}&nbsp;
                                {getTimeZone(item.date)}
                            </InquiryInfoPageDateInfoStyled>
                        )}
                    </InquiryInfoPageChatHistoryNoteInfoStyled>
                    <InquiryInfoPageChatHistoryNoteStyled
                        isOperator={
                            item.type === EActionType.QUESTION ||
                            item.type === EActionType.QUESTION_OPEN ||
                            item.type === EActionType.REPORT_NOTE
                        }
                    >
                        <div dangerouslySetInnerHTML={{ __html: getPrintableText(item.note) }} />
                    </InquiryInfoPageChatHistoryNoteStyled>
                </div>
            );
        },
        [getUserTypeTranslation],
    );

    return (
        <InquiryInfoPageStyled>
            <div className="inquiry-info-page__title">{translationInquiryInfo.chatHistory.title}</div>
            {chat?.lastActions?.length === 0 && !reportNote && (
                <div className="inquiry-info-page__subtitle">{translationInquiryInfo.chatHistory.noHistoryInfo}</div>
            )}
            {chat?.hasMissingActions && chat?.missingActions?.length > 0 && (
                <div>
                    <LinkBlock onClick={() => setShowMissingActions(!isShowMissingActions)}>
                        <InquiryInfoPageMissingChatHistoryStyled>
                            <ImgStyled
                                className="medium"
                                src={isShowMissingActions ? icons.arrowUp : icons.arrowDown}
                            />
                            <InquiryInfoPageMissingChatHistoryTextStyled>
                                {isShowMissingActions
                                    ? translationInquiryInfo.chatHistory.closeMoreActions
                                    : translationInquiryInfo.chatHistory.showMoreActions}
                            </InquiryInfoPageMissingChatHistoryTextStyled>
                        </InquiryInfoPageMissingChatHistoryStyled>
                    </LinkBlock>

                    <InquiryInfoPageChatHistoryMissingBlockStyled isActive={isShowMissingActions}>
                        {chat?.missingActions.map((item, index) => getChatHistoryAction(item, index))}
                    </InquiryInfoPageChatHistoryMissingBlockStyled>
                </div>
            )}
            {chat?.lastActions &&
                chat.lastActions.length > 0 &&
                chat.lastActions.map((item, index) => getChatHistoryAction(item, index))}
            <InquiryInfoPageChatForm
                isShowAnswerBlock={isShowAnswerBlock}
                isShowApproveBlock={isShowApproveBlock}
                noteMaxLength={noteMaxLength}
                {...props}
            />
            {isInquiryClosed && (
                <InquiryInfoPageChatHistoryFinalLineStyled>
                    <InquiryInfoPageChatHistoryFinalLineTextStyled>
                        {translationInquiryInfo.labelFinalLine}
                    </InquiryInfoPageChatHistoryFinalLineTextStyled>
                </InquiryInfoPageChatHistoryFinalLineStyled>
            )}
            {reportNote &&
                getChatHistoryAction({
                    type: EActionType.REPORT_NOTE,
                    note: reportNote,
                })}
        </InquiryInfoPageStyled>
    );
};

export default InquiryInfoPageChatHistory;
